/**
 * State 07 — Sign up: Verify email code.
 *
 * The user submitted email + password in state 06 and the start card
 * called `signUp.prepareEmailAddressVerification({ strategy: "email_code" })`.
 * Here we collect the 6-digit code and call
 * `signUp.attemptEmailAddressVerification({ code })`.
 *   - status complete → setActive + close (or → state 08 if Clerk
 *     requires a username and the user has none).
 *
 * Copy verbatim from C:/tmp/mio-auth-journey.html state 07.
 */

"use client";

import { useState } from "react";

import { useSignUp } from "@clerk/nextjs";

import { activateSession } from "./sessionActivation";

import { IconWarningTriangle } from "./AuthIcons";
import { EllipsisDots } from "./EllipsisDots";
import { IdentityRow } from "./IdentityRow";
import { OtpInput } from "./OtpInput";
import { useResendCountdown } from "./useResendCountdown";
import {
  AuthFlowState,
  AuthStep,
  readClerkError,
} from "./useAuthFlow";
import { trackSignupCompleted } from "@/lib/analytics";

type Props = {
  flow: AuthFlowState;
  setIdentifier: (v: string) => void;
  setError: (e: string | null) => void;
  setSubmitting: (b: boolean) => void;
  markResend: () => void;
  goTo: (s: AuthStep) => void;
  onAuthenticated: () => void;
};

export function SignUpVerifyEmailCard({
  flow,
  setError,
  setSubmitting,
  markResend,
  goTo,
  onAuthenticated,
}: Props) {
  const { signUp, setActive, isLoaded } = useSignUp();
  const [code, setCode] = useState("");
  const resend = useResendCountdown(flow.lastResendAt);

  async function attempt(value: string) {
    if (!isLoaded || !signUp || !setActive) return;
    if (value.length !== 6) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await signUp.attemptEmailAddressVerification({
        code: value,
      });
      if (result.status === "complete" && result.createdSessionId) {
        // 2026-08-29: bounded activation (sessionActivation.ts).
        await activateSession({
          setActive,
          sessionId: result.createdSessionId,
        });
        // C21: through the sign-up gate. Attribution (negotiate vs
        // elsewhere) comes from the marker written when the gate was raised.
        trackSignupCompleted({ method: "sign_up" });
        // Just close the dialog. No navigation: the `needsOnboarding` flag set
        // at sign-up makes the OnboardingGate render the onboarding overlay
        // in place over the current page, so there is no page load and no
        // flash of home. Onboarding shows only for sign-ups (this flag), never
        // for sign-ins.
        onAuthenticated();
        return;
      }
      if (result.status === "missing_requirements") {
        if (result.missingFields?.includes("username")) {
          goTo("signUp.continueUsername");
          return;
        }
      }
      setError("Could not verify the code. Try again.");
    } catch (err) {
      setError(readClerkError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function onResend() {
    if (!isLoaded || !signUp) return;
    if (resend.disabled) return;
    setError(null);
    setSubmitting(true);
    try {
      await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
      markResend();
    } catch (err) {
      setError(readClerkError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void attempt(code);
      }}
    >
      <div className="mio-auth-card-header">
        <h1>Check your inbox</h1>
        <p>Enter the 6-digit code we just emailed you.</p>
      </div>

      {flow.error ? (
        <div className="mio-auth-error" role="alert">
          <IconWarningTriangle />
          <div>{flow.error}</div>
        </div>
      ) : null}

      <IdentityRow
        value={flow.identifier}
        onEdit={() => goTo("signUp.start")}
      />

      <div className="mio-auth-otp-wrap">
        <OtpInput
          value={code}
          onChange={setCode}
          onComplete={attempt}
          disabled={flow.submitting}
        />
        <div className="mio-auth-resend">
          Didn&rsquo;t receive a code?{" "}
          <button
            type="button"
            onClick={() => void onResend()}
            disabled={resend.disabled || flow.submitting}
          >
            Resend
          </button>
          {resend.disabled ? (
            <span className="mio-auth-countdown">{resend.label}</span>
          ) : null}
        </div>
      </div>

      <button
        type="submit"
        className="mio-auth-cta"
        aria-busy={flow.submitting}
        disabled={flow.submitting || code.length !== 6}
      >
        {flow.submitting ? (
          <>
            Verifying
            <EllipsisDots />
          </>
        ) : (
          "Verify email"
        )}
      </button>
      <button
        type="button"
        className="mio-auth-secondary"
        disabled={flow.submitting}
        onClick={() => goTo("signUp.start")}
      >
        Use another method
      </button>
    </form>
  );
}
